import { useState, useEffect } from 'react';
import { supabase, Device } from '../lib/supabase';

export function useDevices() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const addDevice = async (device: Omit<Device, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('devices')
        .insert([{ ...device, serial_number: device.serial_number.toUpperCase() }])
        .select()
        .single();

      if (error) throw error;
      setDevices(prev => [data, ...prev]);
      return { success: true, data };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add device';
      setError(message);
      return { success: false, error: message };
    }
  };

  const updateDevice = async (id: string, updates: Partial<Device>) => {
    try {
      const { data, error } = await supabase
        .from('devices')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setDevices(prev => prev.map(device => device.id === id ? data : device));
      return { success: true, data };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update device';
      setError(message);
      return { success: false, error: message };
    }
  };

  const deleteDevice = async (id: string) => {
    try {
      const { error } = await supabase
        .from('devices')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setDevices(prev => prev.filter(device => device.id !== id));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete device';
      setError(message);
      return { success: false, error: message };
    }
  };

  const bulkImportDevices = async (devices: Omit<Device, 'id' | 'created_at' | 'updated_at'>[]) => {
    try {
      const devicesWithUpperSerial = devices.map(device => ({
        ...device,
        serial_number: device.serial_number.toUpperCase()
      }));

      const { data, error } = await supabase
        .from('devices')
        .insert(devicesWithUpperSerial)
        .select();

      if (error) throw error;
      await fetchDevices(); // Refresh the list
      return { success: true, data, count: data?.length || 0 };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import devices';
      setError(message);
      return { success: false, error: message };
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  return {
    devices,
    loading,
    error,
    fetchDevices,
    addDevice,
    updateDevice,
    deleteDevice,
    bulkImportDevices,
    clearError: () => setError(null)
  };
}